import type { ReactNode } from "react";

import type { AsidePanelConfig, MainPanelConfig, TopbarConfig } from "@/panels/modules/panels/store/panels.store.types";

import { hasRenderableContent } from "@/utils/hasRenderableContent";

const COLLAPSED_HEIGHT_WITH_TABS = 90;
const COLLAPSED_HEIGHT_WITHOUT_TABS = 48;
const COLLAPSED_HEIGHT_WITH_TABS_NO_HEADER = 40;
const COLLAPSED_HEIGHT_WITHOUT_TABS_NO_HEADER = 0;

const MAIN_PANEL_MIN_SIZE = "25%";
const DEFAULT_LEFT_SIDEBAR_SIZE = "15%";
const DEFAULT_LEFT_SIDEBAR_MIN_WIDTH = "180px";
const DEFAULT_LEFT_SIDEBAR_MAX_WIDTH = "240px";
const DEFAULT_TOPBAR_SIZE = "7%";
const DEFAULT_TOPBAR_MIN_HEIGHT = "56px";
const DEFAULT_TOPBAR_MAX_HEIGHT = "56px";
const DEFAULT_RIGHT_SIDEBAR_SIZE = "25%";
const DEFAULT_RIGHT_SIDEBAR_MIN_WIDTH = "240px";
const DEFAULT_RIGHT_SIDEBAR_MAX_WIDTH = "400px";

/**
 * Convert a numeric percentage (e.g. 15) to a v4-compatible percentage string ("15%").
 * react-resizable-panels v4 treats bare numbers as pixels; strings are needed for percentages.
 */
const toPercent = (value: number): string => `${value}%`;

/** Panel sizes calculation helper - provides defaults when config values are not specified */
export const computePanelSizes = ({
    topbarConfig,
    leftSidebarConfig,
    rightSidebarConfig,
}: {
    topbarConfig?: TopbarConfig;
    leftSidebarConfig?: AsidePanelConfig;
    rightSidebarConfig?: AsidePanelConfig;
} = {}) => {
    return {
        leftSidebar: {
            defaultWidth:
                leftSidebarConfig?.defaultWidth != null
                    ? toPercent(leftSidebarConfig.defaultWidth)
                    : DEFAULT_LEFT_SIDEBAR_SIZE,
            minWidth: leftSidebarConfig?.minWidth ?? DEFAULT_LEFT_SIDEBAR_MIN_WIDTH,
            maxWidth: leftSidebarConfig?.maxWidth ?? DEFAULT_LEFT_SIDEBAR_MAX_WIDTH,
        },
        topbar: {
            defaultHeight:
                topbarConfig?.defaultHeight != null ? toPercent(topbarConfig.defaultHeight) : DEFAULT_TOPBAR_SIZE,
            minHeight: topbarConfig?.minHeight ?? DEFAULT_TOPBAR_MIN_HEIGHT,
            maxHeight: topbarConfig?.maxHeight ?? DEFAULT_TOPBAR_MAX_HEIGHT,
        },
        rightSidebar: {
            defaultWidth:
                rightSidebarConfig?.defaultWidth != null
                    ? toPercent(rightSidebarConfig.defaultWidth)
                    : DEFAULT_RIGHT_SIDEBAR_SIZE,
            minWidth: rightSidebarConfig?.minWidth ?? DEFAULT_RIGHT_SIDEBAR_MIN_WIDTH,
            maxWidth: rightSidebarConfig?.maxWidth ?? DEFAULT_RIGHT_SIDEBAR_MAX_WIDTH,
        },
        mainPanels: {
            minSize: MAIN_PANEL_MIN_SIZE,
        },
    };
};

type CalculateTopPanelSizeParams = {
    lastSize?: number;
    topConfig: MainPanelConfig;
    bottomConfig: MainPanelConfig;
    topASlot?: ReactNode;
    topBSlot?: ReactNode;
    bottomASlot?: ReactNode;
    bottomBSlot?: ReactNode;
};
export const calculateTopPanelSize = ({
    lastSize = 50,
    topConfig,
    bottomConfig,
    topASlot,
    topBSlot,
    bottomASlot,
    bottomBSlot,
}: CalculateTopPanelSizeParams): number => {
    const isTopSelectedTabMinimized = topConfig.selectedTab === "a" ? topConfig.a.isMinimized : topConfig.b.isMinimized;
    const isTopPanelClosed = topConfig.a.isClosed && topConfig.b.isClosed;

    const isBottomSelectedTabMinimized =
        bottomConfig.selectedTab === "a" ? bottomConfig.a.isMinimized : bottomConfig.b.isMinimized;
    const isBottomPanelClosed = bottomConfig.a.isClosed && bottomConfig.b.isClosed;

    const topHasContent =
        (!topConfig.a.isClosed && hasRenderableContent(topASlot)) ||
        (!topConfig.b.isClosed && hasRenderableContent(topBSlot));
    const bottomHasContent =
        (!bottomConfig.a.isClosed && hasRenderableContent(bottomASlot)) ||
        (!bottomConfig.b.isClosed && hasRenderableContent(bottomBSlot));

    if (topHasContent && bottomHasContent) {
        return calculateWhenBothHaveContent({
            lastSize,
            isTopSelectedTabMinimized: isTopSelectedTabMinimized,
            isTopPanelClosed: isTopPanelClosed,
            isBottomSelectedTabMinimized: isBottomSelectedTabMinimized,
            isBottomPanelClosed: isBottomPanelClosed,
        });
    } else if (bottomHasContent) {
        return calculateWhenOnlyBottomHasContent({
            isBottomSelectedTabMinimized: isBottomSelectedTabMinimized,
            isBottomPanelClosed: isBottomPanelClosed,
        });
    } else {
        return calculateWhenOnlyTopHasContent({
            isTopSelectedTabMinimized: isTopSelectedTabMinimized,
            isTopPanelClosed: isTopPanelClosed,
        });
    }
};

const calculateWhenBothHaveContent = ({
    lastSize,
    isTopSelectedTabMinimized,
    isTopPanelClosed,
    isBottomSelectedTabMinimized,
    isBottomPanelClosed,
}: {
    lastSize: number;
    isTopSelectedTabMinimized: boolean;
    isTopPanelClosed: boolean;
    isBottomSelectedTabMinimized: boolean;
    isBottomPanelClosed: boolean;
}) => {
    if (isTopSelectedTabMinimized || isTopPanelClosed) {
        return 0;
    } else if (isBottomSelectedTabMinimized || isBottomPanelClosed) {
        return 100;
    } else {
        return lastSize;
    }
};

const calculateWhenOnlyTopHasContent = ({
    isTopSelectedTabMinimized,
    isTopPanelClosed,
}: {
    isTopSelectedTabMinimized: boolean;
    isTopPanelClosed: boolean;
}) => {
    if (isTopSelectedTabMinimized || isTopPanelClosed) {
        return 0;
    } else {
        return 100;
    }
};

const calculateWhenOnlyBottomHasContent = ({
    isBottomSelectedTabMinimized,
    isBottomPanelClosed,
}: {
    isBottomSelectedTabMinimized: boolean;
    isBottomPanelClosed: boolean;
}) => {
    if (isBottomSelectedTabMinimized || isBottomPanelClosed) {
        return 100;
    } else {
        return 0;
    }
};

export const calculatePanelCollapsedHeightPixels = (
    config: MainPanelConfig,
    tabASlot: ReactNode,
    tabBSlot: ReactNode,
): number => {
    if (config.a.isClosed && config.b.isClosed) {
        return 0;
    }

    const bothTabsHaveContent = calcBothTabsHaveContent({
        tabASlot,
        isTabAClosed: config.a.isClosed,
        tabBSlot,
        isTabBClosed: config.b.isClosed,
    });

    return calcCollapsedHeightPixels(bothTabsHaveContent, Boolean(config[config.selectedTab].showHeader));
};

export const calcBothTabsHaveContent = ({
    tabASlot,
    isTabAClosed,
    tabBSlot,
    isTabBClosed,
}: {
    tabASlot: ReactNode;
    isTabAClosed: boolean;
    tabBSlot: ReactNode;
    isTabBClosed: boolean;
}): boolean => {
    const tabAHasContent = !isTabAClosed && hasRenderableContent(tabASlot);
    const tabBHasContent = !isTabBClosed && hasRenderableContent(tabBSlot);
    return tabAHasContent && tabBHasContent;
};

export const calcCollapsedHeightPixels = (bothTabsHaveContent: boolean, showHeader: boolean) => {
    return bothTabsHaveContent
        ? showHeader
            ? COLLAPSED_HEIGHT_WITH_TABS
            : COLLAPSED_HEIGHT_WITH_TABS_NO_HEADER
        : showHeader
          ? COLLAPSED_HEIGHT_WITHOUT_TABS
          : COLLAPSED_HEIGHT_WITHOUT_TABS_NO_HEADER;
};

// Helper functions to compute panel states from configs
export const isPanelFullyClosed = (config: MainPanelConfig, aSlot: ReactNode, bSlot: ReactNode): boolean => {
    const isTabAEmptyOrClosed = !hasRenderableContent(aSlot) || config.a.isClosed;
    const isTabBEmptyOrClosed = !hasRenderableContent(bSlot) || config.b.isClosed;

    return isTabAEmptyOrClosed && isTabBEmptyOrClosed;
};

export const isLeftMainPanelFullyClosed = ({
    leftTop,
    leftTopASlot,
    leftTopBSlot,
    leftBottom,
    leftBottomASlot,
    leftBottomBSlot,
}: {
    leftTop: MainPanelConfig;
    leftTopASlot: ReactNode;
    leftTopBSlot: ReactNode;
    leftBottom: MainPanelConfig;
    leftBottomASlot: ReactNode;
    leftBottomBSlot: ReactNode;
}): boolean => {
    return (
        isPanelFullyClosed(leftTop, leftTopASlot, leftTopBSlot) &&
        isPanelFullyClosed(leftBottom, leftBottomASlot, leftBottomBSlot)
    );
};

export const isOneLeftMainPanelFullyClosed = ({
    leftTop,
    leftTopASlot,
    leftTopBSlot,
    leftBottom,
    leftBottomASlot,
    leftBottomBSlot,
}: {
    leftTop: MainPanelConfig;
    leftTopASlot: ReactNode;
    leftTopBSlot: ReactNode;
    leftBottom: MainPanelConfig;
    leftBottomASlot: ReactNode;
    leftBottomBSlot: ReactNode;
}): boolean => {
    return (
        isPanelFullyClosed(leftTop, leftTopASlot, leftTopBSlot) ||
        isPanelFullyClosed(leftBottom, leftBottomASlot, leftBottomBSlot)
    );
};

export const isRightMainPanelFullyClosed = ({
    rightTop,
    rightTopASlot,
    rightTopBSlot,
    rightBottom,
    rightBottomASlot,
    rightBottomBSlot,
}: {
    rightTop: MainPanelConfig;
    rightTopASlot: ReactNode;
    rightTopBSlot: ReactNode;
    rightBottom: MainPanelConfig;
    rightBottomASlot: ReactNode;
    rightBottomBSlot: ReactNode;
}): boolean => {
    return (
        isPanelFullyClosed(rightTop, rightTopASlot, rightTopBSlot) &&
        isPanelFullyClosed(rightBottom, rightBottomASlot, rightBottomBSlot)
    );
};

export const isOneRightMainPanelFullyClosed = ({
    rightTop,
    rightTopASlot,
    rightTopBSlot,
    rightBottom,
    rightBottomASlot,
    rightBottomBSlot,
}: {
    rightTop: MainPanelConfig;
    rightTopASlot: ReactNode;
    rightTopBSlot: ReactNode;
    rightBottom: MainPanelConfig;
    rightBottomASlot: ReactNode;
    rightBottomBSlot: ReactNode;
}): boolean => {
    return (
        isPanelFullyClosed(rightTop, rightTopASlot, rightTopBSlot) ||
        isPanelFullyClosed(rightBottom, rightBottomASlot, rightBottomBSlot)
    );
};

export const isOneSideOfMainPanelsFullyClosed = ({
    leftTop,
    leftTopASlot,
    leftTopBSlot,
    leftBottom,
    leftBottomASlot,
    leftBottomBSlot,
    rightTop,
    rightTopASlot,
    rightTopBSlot,
    rightBottom,
    rightBottomASlot,
    rightBottomBSlot,
}: {
    leftTop: MainPanelConfig;
    leftTopASlot: ReactNode;
    leftTopBSlot: ReactNode;
    leftBottom: MainPanelConfig;
    leftBottomASlot: ReactNode;
    leftBottomBSlot: ReactNode;
    rightTop: MainPanelConfig;
    rightTopASlot: ReactNode;
    rightTopBSlot: ReactNode;
    rightBottom: MainPanelConfig;
    rightBottomASlot: ReactNode;
    rightBottomBSlot: ReactNode;
}): boolean => {
    return (
        (isPanelFullyClosed(leftTop, leftTopASlot, leftTopBSlot) &&
            isPanelFullyClosed(leftBottom, leftBottomASlot, leftBottomBSlot)) ||
        (isPanelFullyClosed(rightTop, rightTopASlot, rightTopBSlot) &&
            isPanelFullyClosed(rightBottom, rightBottomASlot, rightBottomBSlot))
    );
};

export const calculateLeftPanelSize = ({
    lastSize = 50,
    leftTopAIsClosed,
    leftTopBIsClosed,
    leftBottomAIsClosed,
    leftBottomBIsClosed,
    rightTopAIsClosed,
    rightTopBIsClosed,
    rightBottomAIsClosed,
    rightBottomBIsClosed,
    leftTopA,
    leftTopB,
    leftBottomA,
    leftBottomB,
    rightTopA,
    rightTopB,
    rightBottomA,
    rightBottomB,
}: {
    lastSize?: number;
    leftTopAIsClosed: boolean;
    leftTopBIsClosed: boolean;
    leftBottomAIsClosed: boolean;
    leftBottomBIsClosed: boolean;
    rightTopAIsClosed: boolean;
    rightTopBIsClosed: boolean;
    rightBottomAIsClosed: boolean;
    rightBottomBIsClosed: boolean;
    leftTopA?: ReactNode;
    leftTopB?: ReactNode;
    leftBottomA?: ReactNode;
    leftBottomB?: ReactNode;
    rightTopA?: ReactNode;
    rightTopB?: ReactNode;
    rightBottomA?: ReactNode;
    rightBottomB?: ReactNode;
}): number => {
    // Check if panels have actual content using hasRenderableContent
    const leftHasContent =
        (!leftTopAIsClosed && hasRenderableContent(leftTopA)) ||
        (!leftTopBIsClosed && hasRenderableContent(leftTopB)) ||
        (!leftBottomAIsClosed && hasRenderableContent(leftBottomA)) ||
        (!leftBottomBIsClosed && hasRenderableContent(leftBottomB));

    const rightHasContent =
        (!rightTopAIsClosed && hasRenderableContent(rightTopA)) ||
        (!rightTopBIsClosed && hasRenderableContent(rightTopB)) ||
        (!rightBottomAIsClosed && hasRenderableContent(rightBottomA)) ||
        (!rightBottomBIsClosed && hasRenderableContent(rightBottomB));

    if (leftHasContent && rightHasContent) {
        // Both sides have content - preserve existing behavior
        const isLeftMainPanelFullyClosed =
            leftTopAIsClosed && leftTopBIsClosed && leftBottomAIsClosed && leftBottomBIsClosed;
        const isRightMainPanelFullyClosed =
            rightTopAIsClosed && rightTopBIsClosed && rightBottomAIsClosed && rightBottomBIsClosed;

        if (isLeftMainPanelFullyClosed) return 0;
        return isRightMainPanelFullyClosed ? 100 : lastSize;
    } else if (rightHasContent) {
        // Only right has content - give full space to right
        return 0;
    } else {
        // Only left has content - give full space to left
        return 100;
    }
};

interface ComputePanelStatesParams {
    leftTopConfig: MainPanelConfig;
    leftBottomConfig: MainPanelConfig;
    rightTopConfig: MainPanelConfig;
    rightBottomConfig: MainPanelConfig;
    leftTopA: ReactNode;
    leftTopB: ReactNode;
    leftBottomA: ReactNode;
    leftBottomB: ReactNode;
    rightTopA: ReactNode;
    rightTopB: ReactNode;
    rightBottomA: ReactNode;
    rightBottomB: ReactNode;
}
export const computePanelStates = ({
    leftTopConfig,
    leftBottomConfig,
    rightTopConfig,
    rightBottomConfig,
    leftTopA,
    leftTopB,
    leftBottomA,
    leftBottomB,
    rightTopA,
    rightTopB,
    rightBottomA,
    rightBottomB,
}: ComputePanelStatesParams) => {
    // Panel state computations
    const isLeftMainPanelFullyClosedValue = isLeftMainPanelFullyClosed({
        leftTop: leftTopConfig,
        leftTopASlot: leftTopA,
        leftTopBSlot: leftTopB,
        leftBottom: leftBottomConfig,
        leftBottomASlot: leftBottomA,
        leftBottomBSlot: leftBottomB,
    });
    const isRightMainPanelFullyClosedValue = isRightMainPanelFullyClosed({
        rightTop: rightTopConfig,
        rightTopASlot: rightTopA,
        rightTopBSlot: rightTopB,
        rightBottom: rightBottomConfig,
        rightBottomASlot: rightBottomA,
        rightBottomBSlot: rightBottomB,
    });
    const isOneLeftMainPanelFullyClosedValue = isOneLeftMainPanelFullyClosed({
        leftTop: leftTopConfig,
        leftTopASlot: leftTopA,
        leftTopBSlot: leftTopB,
        leftBottom: leftBottomConfig,
        leftBottomASlot: leftBottomA,
        leftBottomBSlot: leftBottomB,
    });
    const isOneRightMainPanelFullyClosedValue = isOneRightMainPanelFullyClosed({
        rightTop: rightTopConfig,
        rightTopASlot: rightTopA,
        rightTopBSlot: rightTopB,
        rightBottom: rightBottomConfig,
        rightBottomASlot: rightBottomA,
        rightBottomBSlot: rightBottomB,
    });
    const isOneSideOfMainPanelsFullyClosedValue = isOneSideOfMainPanelsFullyClosed({
        leftTop: leftTopConfig,
        leftTopASlot: leftTopA,
        leftTopBSlot: leftTopB,
        leftBottom: leftBottomConfig,
        leftBottomASlot: leftBottomA,
        leftBottomBSlot: leftBottomB,
        rightTop: rightTopConfig,
        rightTopASlot: rightTopA,
        rightTopBSlot: rightTopB,
        rightBottom: rightBottomConfig,
        rightBottomASlot: rightBottomA,
        rightBottomBSlot: rightBottomB,
    });

    // Collapsed height computations
    const leftTopCollapsedHeightPixels = calculatePanelCollapsedHeightPixels(leftTopConfig, leftTopA, leftTopB);
    const rightTopCollapsedHeightPixels = calculatePanelCollapsedHeightPixels(rightTopConfig, rightTopA, rightTopB);
    const leftBottomCollapsedHeightPixels = calculatePanelCollapsedHeightPixels(
        leftBottomConfig,
        leftBottomA,
        leftBottomB,
    );
    const rightBottomCollapsedHeightPixels = calculatePanelCollapsedHeightPixels(
        rightBottomConfig,
        rightBottomA,
        rightBottomB,
    );

    return {
        // Panel states
        isLeftMainPanelFullyClosedValue,
        isRightMainPanelFullyClosedValue,
        isOneLeftMainPanelFullyClosedValue,
        isOneRightMainPanelFullyClosedValue,
        isOneSideOfMainPanelsFullyClosedValue,

        // Collapsed heights
        leftTopCollapsedHeightPixels,
        rightTopCollapsedHeightPixels,
        leftBottomCollapsedHeightPixels,
        rightBottomCollapsedHeightPixels,
    };
};
